//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
// event imports
import { Event } from "./Event";
// other internals
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import * as s from "./Event.styles";
// util
import { WEIRD_VALID_GOOGLE_RESOURCE_CALENDAR_DOMAINS } from "#webapp/src/util/calendar-id.util";
import { logger } from "#webapp/src/util/logger.util";
import { useQuery } from "@apollo/client";
import { getValue, isError } from "@clockwise/client-commons/src/util/errorable.util";
import { isLinkedSmartHold } from "@clockwise/client-commons/src/util/event";
import { isEmailInDomains } from "../../util/email.util";
import { useOrgId } from "../hooks/useOrgId";
import { usePlannerContext } from "../web-app-calendar/Context";
import { EventRenderingError } from "./EventRenderingError";
import { EventRendererQueryDocument } from "./graphql/__generated__/EventRenderer.generated";

type Props = {
  event: {
    externalEventId: string;
    calendarId: string;
  };
};

export const EventRenderer = ({ event }: Props) => {
  const { refetchEvents } = usePlannerContext();
  const { orgId } = useOrgId();
  const externalEventId = event.externalEventId;
  const calendarId = event.calendarId;

  const { data, loading, refetch, error } = useQuery(EventRendererQueryDocument, {
    variables: {
      orgId: orgId!,
      externalEventId,
      externalEventIds: [externalEventId],
      calendarId,
      calendarIds: [calendarId],
      isLinkedSmartHold: isLinkedSmartHold(externalEventId),
      typedCalendarId: calendarId,
    },
    fetchPolicy: "no-cache", // TODO - remove this after cleaning up caching keys. Right now this fetch is causing everything else that fetches
    errorPolicy: "all",
    skip: !orgId || !externalEventId,
    // org to refetch, which both PlannerEventsQuery and PlannerMetadataQuery include.
  });

  const refetchEventAndEvents = React.useCallback(() => {
    void refetchEvents?.();
    void refetch();
  }, [refetch, refetchEvents]);

  const org = getValue(data?.org, "Org");
  const viewer = data?.viewer;

  if (loading && !data) {
    return (
      <div style={s.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    logger.error(`Unable to render event details in sidebar`, error);
    return <EventRenderingError type="generic" />;
  }

  if (!viewer?.user || !org) {
    return null;
  }

  const isMismatch = !isEmailInDomains(calendarId, [
    ...org.domains,
    ...WEIRD_VALID_GOOGLE_RESOURCE_CALENDAR_DOMAINS,
  ]);

  const isUncrawledEvent =
    [
      ...(org.crawledCalendarInfoForCalendars?.synced || []),
      ...(org.crawledCalendarInfoForCalendars?.pending || []),
    ].indexOf(calendarId) < 0;

  if (!calendarId) {
    logger.error(`missing calendarId for event, cannot render`, { externalEventId });
    return <div />;
  }

  if (isMismatch || (isUncrawledEvent && !externalEventId.startsWith("teamevent"))) {
    return <EventRenderingError type="mismatch" />;
  }

  if (isError(org.forceFetchEventParentErrorable)) {
    return org.forceFetchEventParentErrorable.statusCode === 404 ? (
      <EventRenderingError type="missing" />
    ) : (
      <EventRenderingError type="generic" />
    ); // error
  }

  const eventParent = org.forceFetchEventParentErrorable;

  let canRescheduleEvent = false;
  let canAutopilotEvent = false;
  let attendeeNotInDomain = false;
  let organizerNotClockwiseUser = false;
  let isAllDayEvent = false;

  const canRescheduleResponse = getValue(org.canScheduleResponse);
  if (canRescheduleResponse?.list?.length) {
    const canScheduleResponseEntry = canRescheduleResponse?.list[0];

    canRescheduleEvent = canScheduleResponseEntry.canReschedule;
    canAutopilotEvent = canScheduleResponseEntry.canAutopilot;
    attendeeNotInDomain = canScheduleResponseEntry.attendeeNotInDomain;
    organizerNotClockwiseUser = canScheduleResponseEntry.organizerNotClockwiseUser;
    isAllDayEvent = canScheduleResponseEntry.isAllDayEvent;
  }

  let smartHoldEventInfo: ISchema.ISmartHoldEventInfo | null = null;
  if (org.smartHoldEventInfo && org.smartHoldEventInfo.__typename === "SmartHoldEventInfo") {
    smartHoldEventInfo = org.smartHoldEventInfo as ISchema.ISmartHoldEventInfo;
  }

  return (
    <Event
      org={org as ISchema.IOrg}
      user={viewer.user as ISchema.IUser}
      eventParent={eventParent as ISchema.IEventParent}
      smartHoldEventInfo={smartHoldEventInfo}
      calendarId={calendarId}
      canReschedule={canRescheduleEvent}
      canAutopilot={canAutopilotEvent}
      attendeeNotInDomain={attendeeNotInDomain}
      organizerNotClockwiseUser={organizerNotClockwiseUser}
      isAllDayEvent={isAllDayEvent}
      withBorder={false}
      refetch={refetchEventAndEvents}
    />
  );
};
